import { z } from "zod";
import { isCompleteHouseholdCode, normalizeHouseholdCode } from "@/modules/add-new-data/lib/household-code";
import {
  birthdateValidationMessage,
  futureBirthdateValidationMessage,
  isFutureDate,
  isRealBirthdate,
  resolveAgeReferenceDate,
  validateMemberAgeSource,
} from "@/modules/add-new-data/lib/age";
import { getApplicabilityForQuestion } from "@/modules/add-new-data/lib/cleansing";
import { cleanRBIMCode, type RbimCodeQuestionKey } from "@/lib/rbimCodeMap";
import { familyLetters } from "@/lib/family-units";
import { getManilaCalendarDate } from "@/modules/add-new-data/lib/date-encoded";
import { getHouseholdDeathAgeError } from "@/modules/add-new-data/lib/household-death-age-validation";
import { getDuplicateFamilyHeadIssues } from "@/modules/add-new-data/lib/family-head-validation";

export const sexOptions = ["Male", "Female"] as const;

export const relationshipOptions = [
  "Head",
  "Spouse",
  "Son",
  "Daughter",
  "Other relative",
  "Non-relative",
] as const;

export const civilStatusOptions = [
  "Single",
  "Married",
  "Widowed",
  "Separated",
  "Common-law",
  "Divorced",
] as const;

export const householdTypeOptions = ["Household", "Institutional Living Quarter"] as const;

export const interviewResultOptions = ["", "C", "CB", "R"] as const;

export const enrollmentOptions = ["", "Yes", "No"] as const;

export const educationLevelOptions = [
  "",
  "Early Childhood Education",
  "Primary Education",
  "Lower Secondary Education",
  "Upper Secondary Education",
  "Post-Secondary Non-Tertiary Education",
  "Short-Cycle Tertiary Education",
  "Bachelor Level Education",
  "Master Level Education",
  "Doctoral Level Education",
] as const;

export const workStatusOptions = [
  "",
  "Permanent Work",
  "Casual Work",
  "Contractual Work",
  "Individually Owned Business",
  "Shared / Partnership Business",
  "Corporate Business",
] as const;

export const yesNoOptions = ["", "Yes", "No"] as const;

export const residentTypeOptions = ["", "Non-Migrant", "Migrant", "Transient"] as const;

export const legacyExtraFieldStatusOptions = [
  "mapped",
  "acceptedLegacy",
  "unknownPreserved",
  "skippedBlank",
] as const;

export const legacyExtraFieldScopeOptions = [
  "household",
  "member",
  "interview",
  "encoding",
  "metadata",
  "unknown",
] as const;

export const legacyExtraFieldEntrySchema = z.object({
  originalColumnName: z.string(),
  normalizedColumnName: z.string(),
  rawValue: z.string(),
  translatedValue: z.string().optional(),
  destinationField: z.string().optional(),
  mappingStatus: z.enum(legacyExtraFieldStatusOptions),
  scope: z.enum(legacyExtraFieldScopeOptions),
});

export const legacyExtraFieldsSchema = z.object({
  sourceFileName: z.string().optional(),
  sheetName: z.string().optional(),
  rows: z.array(z.object({
    importRowNumber: z.number().int().positive(),
    fields: z.array(legacyExtraFieldEntrySchema),
  })),
});

export const legacyImportStatusOptions = [
  "Clean",
  "Imported with Warning",
  "Needs Review",
  "Skipped",
] as const;
export type LegacyImportStatus = (typeof legacyImportStatusOptions)[number];

export const legacyImportMetadataSchema = z.object({
  sourceSystem: z.string().optional(),
  importMode: z.string().optional(),
  importStatus: z.enum(legacyImportStatusOptions).optional(),
  reviewWarnings: z.array(z.string()).optional(),
  legacyRawData: z.record(z.string(), z.unknown()).optional(),
  importedAt: z.string().optional(),
});

export const legacyMigrationSchema = z.object({
  source: z.string().optional(),
  rowNumbers: z.array(z.number()).optional(),
  rawRows: z.array(z.record(z.string(), z.unknown())).optional(),
  notes: z.array(z.string()).optional(),
}).passthrough();

const requiredText = (label: string) => z.string().trim().min(1, `${label} is required`);
const codedText = (label: string, questionKey: RbimCodeQuestionKey) =>
  requiredText(label).refine((value) => !cleanRBIMCode(questionKey, value).error, {
    message: getCodeValidationMessage(label, questionKey),
  });
const optionalText = () => z.string().trim().optional();
const optionalCodedText = (label: string, questionKey: RbimCodeQuestionKey) =>
  z
    .string()
    .trim()
    .optional()
    .refine((value) => !value || !cleanRBIMCode(questionKey, value).error, {
      message: getCodeValidationMessage(label, questionKey),
    });

export const householdInformationSchema = z.object({
  province: requiredText("Province"),
  provinceCode: z.string().trim().optional(),
  municipality: requiredText("Municipality"),
  municipalityCode: z.string().trim().optional(),
  barangay: requiredText("Barangay"),
  barangayCode: z.string().trim().optional(),
  purok: z.string().trim().optional(),
  address: requiredText("Address"),
  householdCode: requiredText("Household Code")
    .refine(isCompleteHouseholdCode, "Household Code must contain 7 letters or numbers")
    .transform(normalizeHouseholdCode),
  family: z.enum(familyLetters),
  familyRelationshipToHouseholdHead: optionalText(),
  householdType: z.enum(householdTypeOptions),
  respondentName: requiredText("Name of Respondent"),
  householdHead: requiredText("Household Head"),
  totalMembers: requiredText("Total No. of Household Members"),
});

export const householdQuestionsSchema = z.object({
  housingTenure: z.string().trim().optional(),
  lotTenure: z.string().trim().optional(),
  hazardProneArea: z.string().trim().optional(),
  disasterExperiencedPastFiveYears: z.string().trim().optional(),
  knowsNearestEvacuationCenter: z.string().trim().optional(),
  lightingFuel: z.string().trim().optional(),
  cookingFuel: z.string().trim().optional(),
  drinkingWaterSource: z.string().trim().optional(),
  garbageDisposal: z.string().trim().optional(),
  segregatesGarbage: z.string().trim().optional(),
  toiletFacility: z.string().trim().optional(),
  buildingType: z.string().trim().optional(),
  wallMaterial: z.string().trim().optional(),
  femaleDeathPast12Months: z.string().trim().optional(),
  femaleDeathAge: z.string().trim().optional(),
  femaleDeathCause: z.string().trim().optional(),
  childDeathPast12Months: z.string().trim().optional(),
  childDeathAge: z.string().trim().optional(),
  childDeathSex: z.string().trim().optional(),
  childDeathCause: z.string().trim().optional(),
  commonDiseaseCause1: z.string().trim().optional(),
  commonDiseaseCause2: z.string().trim().optional(),
  commonDiseaseCause3: z.string().trim().optional(),
  primaryNeed1: z.string().trim().optional(),
  primaryNeed2: z.string().trim().optional(),
  primaryNeed3: z.string().trim().optional(),
  futureBarangay: z.string().trim().optional(),
  futureMunicipality: z.string().trim().optional(),
  futureProvince: z.string().trim().optional(),
}).superRefine((questions, context) => {
  const q58AgeError = getHouseholdDeathAgeError(
    "q58",
    questions.femaleDeathPast12Months,
    questions.femaleDeathAge,
  );
  const q59AgeError = getHouseholdDeathAgeError(
    "q59",
    questions.childDeathPast12Months,
    questions.childDeathAge,
  );

  if (q58AgeError) {
    context.addIssue({
      code: "custom",
      path: ["femaleDeathAge"],
      message: q58AgeError,
    });
  }

  if (q59AgeError) {
    context.addIssue({
      code: "custom",
      path: ["childDeathAge"],
      message: q59AgeError,
    });
  }
});

export const interviewInformationSchema = z.object({
  visit: z.string(),
  dateOfVisit: z.string().trim().optional(),
  timeStart: z.string().trim().optional(),
  timeEnd: z.string().trim().optional(),
  result: z.enum(interviewResultOptions),
  dateOfNextVisit: z.string().trim().optional(),
  interviewer: z.string().trim().optional(),
  supervisor: z.string().trim().optional(),
});

export const encodingInformationSchema = z.object({
  dateEncoded: z.string().trim().optional(),
  encoder: z.string().trim().optional(),
  supervisor: z.string().trim().optional(),
});

const reportedAgeValidationMessage = "Age must be a whole number from 0 to 120.";
const reportedAgeSchema = z.number({ error: reportedAgeValidationMessage })
  .int(reportedAgeValidationMessage)
  .min(0, reportedAgeValidationMessage)
  .max(120, reportedAgeValidationMessage)
  .nullable();

export const householdMemberSchema = z
  .object({
    residentId: optionalText(),
    firstName: optionalText(),
    middleName: optionalText(),
    lastName: optionalText(),
    sex: optionalCodedText("Sex", "q03_sex"),
    dateOfBirth: optionalText()
      .refine((value) => !value || isRealBirthdate(value), {
        message: birthdateValidationMessage,
      })
      .refine((value) => !value || !isFutureDate(value), {
        message: futureBirthdateValidationMessage,
      }),
    birthdatePrecision: z.enum(["exact", "month-year", "year-only", "age-only", "unknown"]).optional(),
    birthdateOriginalValue: z.string().trim().optional(),
    birthYear: z.number().int().min(1900).nullable().optional(),
    birthMonth: z.number().int().min(1).max(12).nullable().optional(),
    birthDay: z.number().int().min(1).max(31).nullable().optional(),
    importedAge: z.number().int().min(0).max(120).nullable().optional(),
    importedAgeAsOf: z.string().trim().nullable().optional(),
    birthdateSource: z.enum(["manual", "legacy-import"]).optional(),
    dateOfBirthUnknown: z.boolean().optional(),
    reportedAge: reportedAgeSchema.optional(),
    ageAsOfDate: z.string().trim().nullable().optional(),
    ageSource: z.enum(["date-of-birth", "reported"]).nullable().optional(),
    relationshipToHead: optionalCodedText("Relationship to Head", "q02_relationship_to_head"),
    familyUnitAssignment: z.enum(["same", "new", "existing"]).optional(),
    familyUnitId: optionalText(),
    familyUnitNo: optionalText(),
    familyName: optionalText(),
    relationshipToFamilyHead: optionalText(),
    isFamilyHead: z.boolean().optional(),
    civilStatus: optionalCodedText("Civil Status", "q08_marital_status"),
    placeOfBirth: optionalText(),
    nationality: optionalCodedText("Nationality", "q07_nationality"),
    nationalityOther: z.string().trim().optional(),
    religion: optionalText(),
    ethnicity: optionalText(),
    highestEducation: optionalText(),
    currentlyEnrolled: optionalText(),
    schoolLevel: optionalText(),
    placeOfSchool: optionalText(),
    reasonNotAttendingSchool: z.string().trim().optional(),
    reasonNotAttendingSchoolOther: z.string().trim().optional(),
    monthlyIncome: z.string().trim().optional(),
    sourceOfIncome: z.string().trim().optional(),
    sourceOfIncomeOther: z.string().trim().optional(),
    workStatus: z.string().trim().optional(),
    placeOfWork: z.string().trim().optional(),
    placeOfDelivery: z.string().trim().optional(),
    placeOfDeliveryOther: z.string().trim().optional(),
    birthAttendant: z.string().trim().optional(),
    birthAttendantOther: z.string().trim().optional(),
    immunization: z.string().trim().optional(),
    livingChildren: z.string().trim().optional(),
    livingChildrenAlive: z.string().trim().optional(),
    familyPlanningUse: z.string().trim().optional(),
    familyPlanningUseOther: z.string().trim().optional(),
    fpMethodSource: z.string().trim().optional(),
    fpMethodSourceOther: z.string().trim().optional(),
    intentionToUseFp: z.string().trim().optional(),
    intentionToUseFpDetail: z.string().trim().optional(),
    healthInsurance: z.string().trim().optional(),
    healthInsuranceOther: z.string().trim().optional(),
    facilityVisitedPast12Months: z.string().trim().optional(),
    facilityVisitedPast12MonthsOther: z.string().trim().optional(),
    healthFacilityVisitReason: z.string().trim().optional(),
    healthFacilityVisitReasonOther: z.string().trim().optional(),
    disability: z.string().trim().optional(),
    soloParentStatus: z.string().trim().optional(),
    registeredSeniorCitizen: z.string().trim().optional(),
    registeredVoterBarangay: z.string().trim().optional(),
    lengthOfStayInBarangay: z.string().trim().optional(),
    previousResidenceFiveYears: z.string().trim().optional(),
    previousResidenceSixMonths: z.string().trim().optional(),
    residentType: z.string().trim().optional(),
    dateOfTransfer: z.string().trim().optional(),
    reasonLeavingPreviousResidence1: z.string().trim().optional(),
    reasonLeavingPreviousResidence1Other: z.string().trim().optional(),
    reasonLeavingPreviousResidence2: z.string().trim().optional(),
    reasonLeavingPreviousResidence2Other: z.string().trim().optional(),
    reasonLeavingPreviousResidence3: z.string().trim().optional(),
    reasonLeavingPreviousResidence3Other: z.string().trim().optional(),
    returnToPreviousResidence: z.string().trim().optional(),
    returnToPreviousResidenceDetail: z.string().trim().optional(),
    transferReason1: z.string().trim().optional(),
    transferReason1Other: z.string().trim().optional(),
    transferReason2: z.string().trim().optional(),
    transferReason2Other: z.string().trim().optional(),
    transferReason3: z.string().trim().optional(),
    transferReason3Other: z.string().trim().optional(),
    durationOfStayCurrentBarangay: z.string().trim().optional(),
    durationOfStayCurrentBarangayDetail: z.string().trim().optional(),
    hasValidCtc: z.string().trim().optional(),
    ctcIssuedInBarangay: z.string().trim().optional(),
    skillsTrainingInterest: z.string().trim().optional(),
    skills: z.string().trim().optional(),
    skillsOther: z.string().trim().optional(),
    occupation: z.string().trim().optional(),
  })
  .superRefine((member, context) => {
    if (!isMemberStarted(member)) {
      return;
    }

    requireStartedMemberField(member.firstName, "firstName", "First Name", context);
    requireStartedMemberField(member.lastName, "lastName", "Last Name", context);
    requireStartedMemberField(member.sex, "sex", "Sex", context);
    requireStartedMemberField(member.placeOfBirth, "placeOfBirth", "Place of Birth", context);
    requireStartedMemberField(member.nationality, "nationality", "Nationality", context);
    requireStartedMemberField(member.relationshipToHead, "relationshipToHead", "Relationship to Head", context);

    const q12 = cleanRBIMCode("q12_currently_enrolled", member.currentlyEnrolled);
    addNonFilipinoSpecifyIssue(member.nationality, member.nationalityOther, "nationalityOther", context);

    if (!validateMemberAgeSource(member).isValid) {
      return;
    }

    if (getApplicabilityForQuestion(member, "highestEducation") === "applicable") {
      requireStartedMemberField(member.highestEducation, "highestEducation", "Highest Level of Education", context);
      addConditionalCodeIssue(member.highestEducation, "highestEducation", "Highest Level of Education", "q11_highest_education", context);
    }

    if (getApplicabilityForQuestion(member, "currentlyEnrolled") === "applicable") {
      requireStartedMemberField(member.currentlyEnrolled, "currentlyEnrolled", "Currently Enrolled", context);
      addConditionalCodeIssue(member.currentlyEnrolled, "currentlyEnrolled", "Currently Enrolled", "q12_currently_enrolled", context);
    }

    if (
      getApplicabilityForQuestion(member, "schoolLevel") === "applicable" &&
      !q12.error &&
      (q12.code === "1" || q12.code === "2")
    ) {
      requireStartedMemberField(member.schoolLevel, "schoolLevel", "School Level", context);
      addConditionalCodeIssue(member.schoolLevel, "schoolLevel", "School Level", "q13_school_level", context);
      requireStartedMemberField(member.placeOfSchool, "placeOfSchool", "Place of School", context);
    }

    if (getApplicabilityForQuestion(member, "reasonNotAttendingSchool") === "applicable") {
      requireStartedMemberField(
        member.reasonNotAttendingSchool,
        "reasonNotAttendingSchool",
        "Reason for Not Attending School",
        context,
      );
      addConditionalCodeIssue(
        member.reasonNotAttendingSchool,
        "reasonNotAttendingSchool",
        "Reason for Not Attending School",
        "q15_reason_not_attending_school",
        context,
      );
      addOthersSpecifyIssue(
        member.reasonNotAttendingSchool,
        member.reasonNotAttendingSchoolOther,
        "reasonNotAttendingSchool",
        "reasonNotAttendingSchoolOther",
        "Reason for Not Attending School",
        "q15_reason_not_attending_school",
        "20",
        context,
      );
    }
  });

export const addNewDataSchema = z
  .object({
    household: householdInformationSchema,
    householdQuestions: householdQuestionsSchema,
    interviews: z.array(interviewInformationSchema).length(2),
    encoding: encodingInformationSchema,
    members: z.array(householdMemberSchema).min(1, "Add at least one household member"),
    legacyExtraFields: legacyExtraFieldsSchema.optional(),
    legacyMigration: legacyMigrationSchema.optional(),
    ...legacyImportMetadataSchema.shape,
  })
  .superRefine((values, context) => {
    if (
      values.household.family !== "A" &&
      !values.household.familyRelationshipToHouseholdHead?.trim()
    ) {
      context.addIssue({
        code: "custom",
        path: ["household", "familyRelationshipToHouseholdHead"],
        message: `Relationship of Family ${values.household.family} to the Household Head is required.`,
      });
    }

    addMissingHeadIssue(values, context);
    addDuplicateRelationshipIssue(values, context, "01", "Only one household head is allowed.");
    addDuplicateRelationshipIssue(values, context, "02", "Only one spouse is allowed for the household head.");
    getDuplicateFamilyHeadIssues(values.members).forEach((issue) => {
      issue.memberIndexes.forEach((memberIndex) => {
        context.addIssue({
          code: "custom",
          path: ["members", memberIndex, "isFamilyHead"],
          message: issue.message,
        });
      });
    });
    const referenceDate = resolveAgeReferenceDate(values.interviews, { members: values.members });

    values.members.forEach((member, index) => {
      if (!isMemberStarted(member)) {
        return;
      }

      const ageValidation = validateMemberAgeSource(member, referenceDate);

      if (!ageValidation.isValid && ageValidation.field && ageValidation.message) {
        context.addIssue({
          code: "custom",
          path: ["members", index, ageValidation.field],
          message: ageValidation.message,
        });
      }
    });
  });

function isMemberStarted(member: Record<string, unknown>) {
  return member.dateOfBirthUnknown === true ||
    (member.reportedAge !== null && typeof member.reportedAge !== "undefined" && String(member.reportedAge).trim() !== "") ||
    ["firstName", "middleName", "lastName", "sex", "dateOfBirth", "relationshipToHead"]
      .some((field) => typeof member[field] === "string" && String(member[field]).trim().length > 0);
}

function requireStartedMemberField(
  value: string | undefined,
  field: string,
  label: string,
  context: z.RefinementCtx,
) {
  if (value?.trim()) {
    return;
  }

  context.addIssue({
    code: "custom",
    path: [field],
    message: `${label} is required`,
  });
}

function addConditionalCodeIssue(
  value: string | undefined,
  field: string,
  label: string,
  questionKey: RbimCodeQuestionKey,
  context: z.RefinementCtx,
) {
  if (!value?.trim()) {
    return;
  }

  const result = cleanRBIMCode(questionKey, value);

  if (!result.error) {
    return;
  }

  context.addIssue({
    code: "custom",
    path: [field],
    message: getCodeValidationMessage(label, questionKey),
  });
}

function addOthersSpecifyIssue(
  value: string | undefined,
  specifyValue: string | undefined,
  field: string,
  specifyField: string,
  label: string,
  questionKey: RbimCodeQuestionKey,
  othersCode: string,
  context: z.RefinementCtx,
) {
  if (!value?.trim()) {
    return;
  }

  const result = cleanRBIMCode(questionKey, value);

  if (result.error || result.code !== othersCode) {
    return;
  }

  const normalizedValue = value.trim().toUpperCase();

  if (
    normalizedValue !== othersCode &&
    !new RegExp(`^0*${othersCode}(?:\\s*(?:-|:|,)\\s*|\\s+)\\S+`, "i").test(value.trim())
  ) {
    if (specifyValue?.trim()) {
      return;
    }

    context.addIssue({
      code: "custom",
      path: [specifyField],
      message: `${label} requires a specified response when Others is selected`,
    });
  }
}

function addNonFilipinoSpecifyIssue(
  value: string | undefined,
  specifyValue: string | undefined,
  specifyField: string,
  context: z.RefinementCtx,
) {
  if (!value?.trim()) {
    return;
  }

  const result = cleanRBIMCode("q07_nationality", value);

  if (result.error || result.code !== "2") {
    return;
  }

  if (/^0*2(?:\s*[-,:]\s*|\s+).+/i.test(value.trim()) || specifyValue?.trim()) {
    return;
  }

  context.addIssue({
    code: "custom",
    path: [specifyField],
    message: "Please specify the nationality.",
  });
}

function addMissingHeadIssue(values: AddNewDataFormValues, context: z.RefinementCtx) {
  const hasHead = values.members.some((member) => {
    const result = cleanRBIMCode("q02_relationship_to_head", member.relationshipToHead);

    return !result.error && result.code === "01";
  });

  if (hasHead) {
    return;
  }

  context.addIssue({
    code: "custom",
    path: ["members"],
    message: "One household head is required.",
  });
}

function addDuplicateRelationshipIssue(
  values: AddNewDataFormValues,
  context: z.RefinementCtx,
  code: "01" | "02",
  message: string,
) {
  const duplicateIndexes = values.members
    .map((member, index) => {
      const result = cleanRBIMCode("q02_relationship_to_head", member.relationshipToHead);

      return !result.error && result.code === code ? index : -1;
    })
    .filter((index) => index >= 0);

  if (duplicateIndexes.length <= 1) {
    return;
  }

  duplicateIndexes.forEach((index) => {
    context.addIssue({
      code: "custom",
      path: ["members", index, "relationshipToHead"],
      message,
    });
  });
}

export type AddNewDataFormValues = z.infer<typeof addNewDataSchema>;
export type HouseholdMemberFormValues = z.infer<typeof householdMemberSchema>;
export type LegacyExtraFieldEntry = z.infer<typeof legacyExtraFieldEntrySchema>;
export type LegacyExtraFields = z.infer<typeof legacyExtraFieldsSchema>;

export const emptyMember: HouseholdMemberFormValues = {
  residentId: "",
  firstName: "",
  middleName: "",
  lastName: "",
  sex: "",
  dateOfBirth: "",
  birthdatePrecision: "unknown",
  dateOfBirthUnknown: false,
  reportedAge: null,
  ageAsOfDate: null,
  ageSource: null,
  relationshipToHead: "",
  familyUnitAssignment: "same",
  familyUnitId: "",
  familyUnitNo: "FAM-001",
  familyName: "Family A",
  relationshipToFamilyHead: "",
  isFamilyHead: false,
  civilStatus: "",
  placeOfBirth: "",
  nationality: "",
  nationalityOther: "",
  religion: "",
  ethnicity: "",
  highestEducation: "",
  currentlyEnrolled: "",
  schoolLevel: "",
  placeOfSchool: "",
  reasonNotAttendingSchool: "",
  reasonNotAttendingSchoolOther: "",
  monthlyIncome: "",
  sourceOfIncome: "",
  sourceOfIncomeOther: "",
  workStatus: "",
  placeOfWork: "",
  placeOfDelivery: "",
  placeOfDeliveryOther: "",
  birthAttendant: "",
  birthAttendantOther: "",
  immunization: "",
  livingChildren: "",
  livingChildrenAlive: "",
  familyPlanningUse: "",
  familyPlanningUseOther: "",
  fpMethodSource: "",
  fpMethodSourceOther: "",
  intentionToUseFp: "",
  intentionToUseFpDetail: "",
  healthInsurance: "",
  healthInsuranceOther: "",
  facilityVisitedPast12Months: "",
  facilityVisitedPast12MonthsOther: "",
  healthFacilityVisitReason: "",
  healthFacilityVisitReasonOther: "",
  disability: "",
  soloParentStatus: "",
  registeredSeniorCitizen: "",
  registeredVoterBarangay: "",
  lengthOfStayInBarangay: "",
  previousResidenceFiveYears: "",
  previousResidenceSixMonths: "",
  residentType: "",
  dateOfTransfer: "",
  reasonLeavingPreviousResidence1: "",
  reasonLeavingPreviousResidence1Other: "",
  reasonLeavingPreviousResidence2: "",
  reasonLeavingPreviousResidence2Other: "",
  reasonLeavingPreviousResidence3: "",
  reasonLeavingPreviousResidence3Other: "",
  returnToPreviousResidence: "",
  returnToPreviousResidenceDetail: "",
  transferReason1: "",
  transferReason1Other: "",
  transferReason2: "",
  transferReason2Other: "",
  transferReason3: "",
  transferReason3Other: "",
  durationOfStayCurrentBarangay: "",
  durationOfStayCurrentBarangayDetail: "",
  hasValidCtc: "",
  ctcIssuedInBarangay: "",
  skillsTrainingInterest: "",
  skills: "",
  skillsOther: "",
  occupation: "",
};

function getCodeValidationMessage(label: string, questionKey: RbimCodeQuestionKey) {
  if (questionKey === "q03_sex") {
    return "Please select or enter a valid sex.";
  }

  return `${label} must use a valid code or label`;
}

export const defaultAddNewDataValues: AddNewDataFormValues = {
  household: {
    province: "",
    provinceCode: "",
    municipality: "",
    municipalityCode: "",
    barangay: "",
    barangayCode: "",
    purok: "",
    address: "",
    householdCode: "",
    family: "A",
    familyRelationshipToHouseholdHead: "",
    householdType: "Household",
    respondentName: "",
    householdHead: "",
    totalMembers: "01",
  },
  householdQuestions: {
    housingTenure: "",
    lotTenure: "",
    hazardProneArea: "",
    disasterExperiencedPastFiveYears: "",
    knowsNearestEvacuationCenter: "",
    lightingFuel: "",
    cookingFuel: "",
    drinkingWaterSource: "",
    garbageDisposal: "",
    segregatesGarbage: "",
    toiletFacility: "",
    buildingType: "",
    wallMaterial: "",
    femaleDeathPast12Months: "",
    femaleDeathAge: "",
    femaleDeathCause: "",
    childDeathPast12Months: "",
    childDeathAge: "",
    childDeathSex: "",
    childDeathCause: "",
    commonDiseaseCause1: "",
    commonDiseaseCause2: "",
    commonDiseaseCause3: "",
    primaryNeed1: "",
    primaryNeed2: "",
    primaryNeed3: "",
    futureBarangay: "",
    futureMunicipality: "",
    futureProvince: "",
  },
  interviews: [
    {
      visit: "1st Visit",
      dateOfVisit: "",
      timeStart: "",
      timeEnd: "",
      result: "",
      dateOfNextVisit: "",
      interviewer: "",
      supervisor: "",
    },
    {
      visit: "2nd Visit",
      dateOfVisit: "",
      timeStart: "",
      timeEnd: "",
      result: "",
      dateOfNextVisit: "",
      interviewer: "",
      supervisor: "",
    },
  ],
  encoding: {
    dateEncoded: "",
    encoder: "",
    supervisor: "",
  },
  members: [emptyMember],
};

export function initializeAddNewDataValues(
  existingValues?: AddNewDataFormValues | null,
  now = new Date(),
): AddNewDataFormValues {
  const values = structuredClone(existingValues ?? defaultAddNewDataValues);

  if (!(values.encoding.dateEncoded ?? "").trim()) {
    values.encoding.dateEncoded = getManilaCalendarDate(now);
  }

  return values;
}
